import { type FragmentType, useFragment } from "@repo/graphql-types/fragment-masking";
import { graphql } from "@repo/graphql-types/gql";
import { formatCurrency, formatIgnoringTimezone } from "@repo/lib";
import { parseISO } from "date-fns";
import {
  CpsAlertV2Description,
  CpsAlertV2IconArea,
  CpsAlertV2Root,
  CpsAlertV2TextArea,
  CpsAlertV2Title,
} from "corpus";
import { InformationCircle } from "@repo/icons";
import { BillingItemDiscount } from "@/components/billing-item-discount";
import { InfoSection } from "@/components/info-section";
import { BillingItemDetailsQueryFragment } from "@/components/billing-item-details";
import { BillingItemDetailsAssistDownloadButton } from "@/components/billing-item-details-assist-download-button";
import { getAssistVariablePrices } from "@/lib/assist-utils";

export const AssistBillingBatchQueryFragment = graphql(/* GraphQL */ `
  fragment AssistBillingBatchQueryFragment on nuntius_lote_contatos_cobranca { 
    contatosPorDia {
      totalCobravel
      totalDigitalCobravel
      totalHumanoCobravel
      data
    }
  }
`);

export const AssistBillingBatchVariablePricesFragment = graphql(/* GraphQL */ `
  fragment AssistBillingBatchVariablePricesFragment on pacoteServicoPreco {
    pacotesServicosPrecosVariaveis {
      valorUnitario
      codPacoteServicosPrecoVariavel
    }
  }
`);

interface BillingItemDetailsAssistProps {
  billingData: FragmentType<typeof BillingItemDetailsQueryFragment>;
  assistBillingBatchData: FragmentType<typeof AssistBillingBatchQueryFragment>;
  assistBillingBatchVariablePricesData: FragmentType<
    typeof AssistBillingBatchVariablePricesFragment
  >;
}

export const BillingItemDetailsAssist = ({
  billingData,
  assistBillingBatchData,
  assistBillingBatchVariablePricesData,
}: BillingItemDetailsAssistProps): JSX.Element => {
  const billingItemDetails = useFragment(BillingItemDetailsQueryFragment, billingData);

  const assistBillingBatch = useFragment(
    AssistBillingBatchQueryFragment,
    assistBillingBatchData,
  );

  const assistBillingBatchVariablePrices = useFragment(
    AssistBillingBatchVariablePricesFragment,
    assistBillingBatchVariablePricesData,
  );

  const formattedAppointmentDate = formatIgnoringTimezone(
    parseISO(assistBillingBatch.contatosPorDia.data),
    "dd/MM/yyyy",
  );

  const { digitalContactVariablePrice, humanContactVariablePrice } =
    getAssistVariablePrices(
      assistBillingBatchVariablePrices.pacotesServicosPrecosVariaveis,
    );

  const section = {
    title: "",
    fields: [
      {
        label: "Utilização",
        value: formattedAppointmentDate,
      },
      {
        label: "Contatos atendidos",
        value: String(assistBillingBatch.contatosPorDia.totalCobravel),
      },
      {
        label: "Cobrança por contato atendido com:",
        value: " ",
      },
    ],
  };

  const contactTypes: {
    label: string;
    key: "totalDigitalCobravel" | "totalHumanoCobravel";
    price: number | null;
  }[] = [
    {
      label: "Atendimento Digital",
      key: "totalDigitalCobravel",
      price: digitalContactVariablePrice ?? null,
    },
    {
      label: "Atendimento Humano",
      key: "totalHumanoCobravel",
      price: humanContactVariablePrice ?? null,
    },
  ];

  const contactDetailsSection = {
    title: "",
    fields: contactTypes.map(({ label, key, price }) => ({
      label,
      labelTag:
        price !== null
          ? `${assistBillingBatch.contatosPorDia[key]} X ${formatCurrency(price)}`
          : "",
      value:
        price !== null
          ? formatCurrency(assistBillingBatch.contatosPorDia[key] * price)
          : null,
    })),
  };

  return (
    <div className="flex flex-col items-start justify-center">
      <p className="font-normal text-sm text-neutral-500">
        {billingItemDetails.nomeTipoMovimentacao}
      </p>
      <BillingItemDiscount
        billingItemValue={Number(
          billingItemDetails.valor + billingItemDetails.valorAcrescimo,
        )}
        discountValue={Number(billingItemDetails.valorDesconto)}
      />
      <p className="font-medium text-2xl text-neutral-700">
        {formatCurrency(Number(billingItemDetails.valor))}
      </p>

      <div className="w-full mt-6">
        <p className="font-medium">Detalhes dos contatos atendidos</p>

        <InfoSection section={section} includeLastLine={false} lineColorLevel={100} />

        <div className="mt-3 bg-neutral-50 rounded-40 w-full border-b border-spacing-30 border-b-neutral-100">
          <div className="px-3 text-sm">
            <InfoSection
              section={contactDetailsSection}
              includeLastLine
              lineColorLevel={100}
            />
          </div>
          <div className="flex justify-between px-3 py-4 font-medium">
            <p>Total</p>
            <p>{formatCurrency(Number(billingItemDetails.valor))}</p>
          </div>
        </div>

        <div className="mt-3 border-b border-spacing-30 border-b-neutral-100" />
      </div>

      <CpsAlertV2Root type="info" htmlProps={{ className: "mt-6" }}>
        <div className="flex">
          <CpsAlertV2IconArea>
            <InformationCircle size={24} className="fill-secondary-500" />
          </CpsAlertV2IconArea>
          <CpsAlertV2TextArea>
            <CpsAlertV2Title type="info">
              Esses são os contatos atendidos do dia.
            </CpsAlertV2Title>
            <CpsAlertV2Description>
              Use o botão abaixo para ver os detalhes:{" "}
              <span className="font-bold">
                data de inicio e fim, tipo, motivo e código do contato
              </span>
              . Se precisar, fale com nosso time de atendimento.
            </CpsAlertV2Description>
          </CpsAlertV2TextArea>
        </div>
      </CpsAlertV2Root>

      <BillingItemDetailsAssistDownloadButton />
    </div>
  );
};
